import classNames from "classnames";
import { FC, memo, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { ReactComponent as TrashSVG } from "assets/images/common/trash.svg";
import { ReactComponent as PlusSVG } from "assets/images/common/plus.svg";

import { showToast } from "store/slices/toastSlice";

import styles from "./styles.module.scss";

export type ImageFile = {
  id?: string;
  file?: File;
  url?: string;
};

type PropTypes = {
  images: ImageFile[];
  onChange: (images: ImageFile[]) => void;
  maxFiles?: number;
  maxSize?: number;
  allowedTypes?: string[];
  error?: string;
  className?: string;
};

const formatMegabytes = (bytes: number) => {
  const mb = bytes / (1024 * 1024);
  return Number.isInteger(mb) ? `${mb}` : mb.toFixed(1);
};

const ImageUpload: FC<PropTypes> = ({
  images,
  onChange,
  maxFiles,
  maxSize,
  allowedTypes,
  error,
  className,
}) => {
  const dispatch = useDispatch();
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrls, setPreviewUrls] = useState<Map<File, string>>(new Map());
  const previewUrlsRef = useRef(previewUrls);
  previewUrlsRef.current = previewUrls;

  useEffect(() => {
    return () => {
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const getPreviewUrl = useCallback(
    (image: ImageFile): string | undefined => {
      if (image.url) return image.url;
      if (image.file) return previewUrls.get(image.file);
      return undefined;
    },
    [previewUrls],
  );

  const handleAdd = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selected = Array.from(e.target.files || []);
      if (inputRef.current) inputRef.current.value = "";
      if (!selected.length) return;

      const rejectedByType: string[] = [];
      const rejectedBySize: string[] = [];
      const accepted: File[] = [];

      for (const file of selected) {
        if (allowedTypes && !allowedTypes.includes(file.type)) {
          rejectedByType.push(file.name);
          continue;
        }
        if (maxSize !== undefined && file.size > maxSize) {
          rejectedBySize.push(file.name);
          continue;
        }
        accepted.push(file);
      }

      const remainingSlots =
        maxFiles !== undefined ? Math.max(0, maxFiles - images.length) : accepted.length;
      const filesToKeep = accepted.slice(0, remainingSlots);
      const rejectedByLimit = accepted.length - filesToKeep.length;

      if (rejectedByType.length) {
        dispatch(
          showToast({
            id: `image-upload-type-${Date.now()}`,
            message: "Only JPG, PNG, or WEBP images are allowed.",
            type: "error",
          }),
        );
      }

      if (rejectedBySize.length && maxSize !== undefined) {
        dispatch(
          showToast({
            id: `image-upload-size-${Date.now()}`,
            message: `Each image must be ${formatMegabytes(maxSize)} MB or smaller.`,
            type: "error",
          }),
        );
      }

      if (rejectedByLimit > 0 && maxFiles !== undefined) {
        dispatch(
          showToast({
            id: `image-upload-count-${Date.now()}`,
            message: `You can upload up to ${maxFiles} images.`,
            type: "error",
          }),
        );
      }

      if (!filesToKeep.length) return;

      const newPreviewUrls = new Map(previewUrls);
      const newImages: ImageFile[] = filesToKeep.map((file) => {
        const url = URL.createObjectURL(file);
        newPreviewUrls.set(file, url);
        return { file, url };
      });

      setPreviewUrls(newPreviewUrls);
      onChange([...images, ...newImages]);
    },
    [images, onChange, maxFiles, maxSize, allowedTypes, previewUrls, dispatch],
  );

  const handleRemove = useCallback(
    (index: number) => {
      const image = images[index];
      if (image.file) {
        const url = previewUrls.get(image.file);
        if (url) {
          URL.revokeObjectURL(url);
          const newPreviewUrls = new Map(previewUrls);
          newPreviewUrls.delete(image.file);
          setPreviewUrls(newPreviewUrls);
        }
      }
      onChange(images.filter((_, i) => i !== index));
    },
    [images, onChange, previewUrls],
  );

  const canAdd = maxFiles === undefined || images.length < maxFiles;
  const acceptAttr = allowedTypes?.length ? allowedTypes.join(",") : "image/*";

  return (
    <div className={classNames(styles.imageUpload, className)}>
      <div className={styles.grid}>
        {images.map((image, index) => {
          const src = getPreviewUrl(image);
          return (
            <div key={image.id || index} className={styles.thumbnail}>
              {src && <img src={src} alt="" className={styles.image} />}
              <button
                type="button"
                className={styles.removeBtn}
                onClick={() => handleRemove(index)}
              >
                <TrashSVG />
              </button>
            </div>
          );
        })}

        {canAdd && (
          <button type="button" className={styles.addBtn} onClick={handleAdd}>
            <PlusSVG />
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={acceptAttr}
        multiple
        className={styles.hiddenInput}
        onChange={handleFileChange}
      />

      {!!error && <div className={styles.error}>{error}</div>}
    </div>
  );
};

export default memo(ImageUpload);
